import { useUserStore } from '@/stores/userStore'

import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type CustomParamsSerializer
} from 'axios'

export type RequestConfig<T = unknown> = AxiosRequestConfig<T | FormData>
export type ResponseConfig<T = unknown> = AxiosResponse<T>
export type ResponseErrorConfig<TErr = unknown> = AxiosError<TErr>
export type Client = <TData = unknown, _TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>
) => Promise<ResponseConfig<TData>>

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_NRAI_BASE_URL
})

axiosInstance.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

export const customParamsSerializer: CustomParamsSerializer = params => {
  if (!params) return ''

  const parts: string[] = []
  const encode = (val: string | number | boolean) => encodeURIComponent(val).replace(/%20/g, '+')

  const serialize = (obj: Record<string, unknown>, prefix?: string) => {
    Object.keys(obj).forEach(key => {
      const value = obj[key]
      const paramKey = prefix ? `${prefix}.${key}` : key

      if (value === null || typeof value === 'undefined') {
        return
      }

      if (Array.isArray(value)) {
        value.forEach(v => {
          parts.push(`${encode(paramKey)}=${encode(v)}`)
        })
      } else if (typeof value === 'object') {
        serialize(value as Record<string, unknown>, paramKey)
      } else {
        parts.push(`${encode(paramKey)}=${encode(value as string | number | boolean)}`)
      }
    })
  }

  serialize(params)
  return parts.join('&')
}

axiosInstance.defaults.paramsSerializer = customParamsSerializer

axiosInstance.interceptors.request.use(request => {
  const userStore = useUserStore()
  if (userStore.accessToken) {
    request.headers['Authorization'] = `Bearer ${userStore.accessToken}`
  }
  return request
})

const client: Client = async <TData, TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>
): Promise<ResponseConfig<TData>> => {
  const promise = axiosInstance
    .request<TVariables, ResponseConfig<TData>>({ ...config })
    .catch((e: AxiosError<TError>) => {
      throw e
    })

  return promise
}

export default client
